import { Button, DetailList, formatDateTime, formatPhone } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { HoldToConfirm } from "@majitap/ui/arc/hold-to-confirm/hold-to-confirm";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BadgeCheck } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useActivity } from "../../activity";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import {
  lastPayoutChange,
  type Partner,
  type Payout,
  type PayoutFields,
  partnerActions,
  payoutState,
} from "../../partners";
import { ConfirmDialog } from "../shared/confirm";
import styles from "./partners.module.css";

/** How long the approve button must be held: money moves to this destination. */
export const PAYOUT_HOLD_MS = 1500;

/**
 * Where the partner is paid. Any change waits for a second person: whoever changed the details
 * can't approve them (`PAYOUT_SELF_APPROVAL`), and payouts stop until they're approved. The
 * previous details and who changed them come from the audit trail (`staff.manage`).
 */
export function PayoutPanel({
  partner: p,
  canReadHistory,
  meId,
}: {
  partner: Partner;
  canReadHistory: boolean;
  meId: string | undefined;
}): ReactNode {
  const format = useCompanyFormat();
  const activity = useActivity("partner", p.id, canReadHistory);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const queryClient = useQueryClient();
  const state = payoutState(p.payout);
  const change = activity.data ? lastPayoutChange(activity.data) : null;
  const who = (userId: string | null, name: string | null, byPartner: boolean) => {
    if (!userId) return "the system";
    if (userId === meId) return "you";
    if (byPartner) return name ? `${name} (${p.name})` : p.name;
    return name || "a staff member";
  };
  const changedByMe = change !== null && change.by !== null && change.by === meId;

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span>
          {state === "none" ? <Badge tone="neutral">Not set</Badge> : null}
          {state === "waiting" ? <Badge tone="warning">Waiting for approval</Badge> : null}
          {state === "approved" ? <Badge tone="success">Approved</Badge> : null}
        </span>
        <span className={styles.actions}>
          <Link to="/pricing" search={{ partner_id: p.id, when: "any" }} className={styles.link}>
            Their rates
          </Link>
          <Link to="/payouts" search={{ partner_id: p.id, status: "all" }} className={styles.link}>
            Their payout requests
          </Link>
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            Change payout details
          </Button>
          {state === "none" ? null : (
            <Button variant="secondary" size="sm" onClick={() => setRemoving(true)}>
              Remove them
            </Button>
          )}
        </span>
      </div>
      {state === "waiting" ? (
        <Alert tone="warning" title="Payouts to this partner are on hold">
          Check the new details with the partner on a number you already know, not one in the request. Then approve
          them.
        </Alert>
      ) : null}

      <div className={styles.sideBySide}>
        <section className={styles.card} aria-label={state === "waiting" ? "New details" : "Current details"}>
          <h3 className={styles.cardTitle}>{state === "waiting" ? "New details" : "Current details"}</h3>
          <PayoutDetails payout={p.payout} />
          {p.payout.changed_at ? (
            <p className={styles.muted}>
              Changed {formatDateTime(p.payout.changed_at, format)}
              {change ? ` by ${who(change.by, change.byName, change.byPartner)}` : ""}
              {p.payout.approved_at ? ` · approved ${formatDateTime(p.payout.approved_at, format)}` : ""}
            </p>
          ) : null}
        </section>
        {state === "waiting" && change?.before ? (
          <section className={styles.card} aria-label="Before the change">
            <h3 className={styles.cardTitle}>Before the change</h3>
            <PayoutDetails payout={change.before} />
          </section>
        ) : null}
      </div>
      {state === "waiting" && !canReadHistory ? (
        <p className={styles.muted}>
          Who changed them, and what they were before, is in the activity log (staff.manage).
        </p>
      ) : null}

      {state === "waiting" ? (
        changedByMe ? (
          <Alert tone="info" title="Someone else must approve these">
            You made this change, so another person with partners.manage approves it.
          </Alert>
        ) : (
          <ApprovePayout partnerId={p.id} />
        )
      ) : null}
      {editing ? <PayoutDialog partner={p} onClose={() => setEditing(false)} /> : null}
      {removing ? (
        <ConfirmDialog
          title={`Remove ${p.name}'s payout details?`}
          description="Its payouts stop until new details are added and approved. Use it when the details are wrong or no longer safe, and you don't have the new ones yet."
          action="Remove them"
          danger
          run={() =>
            partnerActions.update(p.id, {
              payout_method: null,
              payout_destination: null,
              payout_account_name: null,
              payout_bank_code: null,
            })
          }
          onDone={() => queryClient.invalidateQueries({ queryKey: ["partner", p.id] })}
          onClose={() => setRemoving(false)}
        />
      ) : null}
    </div>
  );
}

function PayoutDetails({ payout }: { payout: Partial<Payout> }) {
  return (
    <DetailList
      items={[
        { label: "Method", value: payout.method === "mpesa" ? "M-Pesa" : payout.method === "bank" ? "Bank" : null },
        {
          label: payout.method === "bank" ? "Account number" : "M-Pesa number",
          value: payout.destination
            ? payout.method === "mpesa"
              ? formatPhone(payout.destination)
              : payout.destination
            : null,
        },
        { label: "Account name", value: payout.account_name ?? null },
        ...(payout.method === "bank" ? [{ label: "Bank code", value: payout.bank_code ?? null }] : []),
      ]}
    />
  );
}

/** Approving is a money control: a press and hold, never a single click. */
export function ApprovePayout({ partnerId }: { partnerId: string }) {
  const queryClient = useQueryClient();
  const [holding, setHolding] = useState(false);
  const [confirmed, setConfirmed] = useState<boolean | undefined>(undefined);
  const approve = useMutation({
    mutationFn: () => partnerActions.approvePayout(partnerId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["partner", partnerId] });
      await queryClient.invalidateQueries({ queryKey: ["partners"] });
      await queryClient.invalidateQueries({ queryKey: ["activity", "partner", partnerId] });
    },
    // the hold completed but the API refused: rewind the button so it can be tried again
    onError: () => setConfirmed(false),
  });
  return (
    <div className={styles.section}>
      <div className={styles.actions}>
        <HoldToConfirm
          label="Hold to approve payout details"
          confirmedLabel="Approved"
          tone="neutral"
          icon={<BadgeCheck size={16} strokeWidth={1.75} />}
          duration={PAYOUT_HOLD_MS}
          confirmed={confirmed}
          onHoldChange={setHolding}
          onConfirm={() => {
            setConfirmed(undefined);
            approve.mutate();
          }}
        />
        <span className={styles.muted} aria-hidden="true">
          {holding ? "Keep holding" : ""}
        </span>
      </div>
      {approve.error ? (
        <Alert tone="danger" title="Not approved">
          {messageFor(errorOf(approve.error))}
        </Alert>
      ) : null}
    </div>
  );
}

/** Staff can change the details too; then someone else approves them. */
function PayoutDialog({ partner, onClose }: { partner: Partner; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [method, setMethod] = useState<"mpesa" | "bank">(partner.payout.method ?? "mpesa");
  const [destination, setDestination] = useState(partner.payout.destination ?? "");
  const [accountName, setAccountName] = useState(partner.payout.account_name ?? "");
  const [bankCode, setBankCode] = useState(partner.payout.bank_code ?? "");
  const valid = destination.trim() && (method === "mpesa" || bankCode.trim());
  const mutation = useMutation({
    mutationFn: () => {
      const body: PayoutFields = {
        payout_method: method,
        payout_destination: destination.trim(),
        payout_account_name: accountName.trim() || null,
        payout_bank_code: method === "bank" ? bankCode.trim() : null,
      };
      return partnerActions.update(partner.id, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["partner", partner.id] });
      await queryClient.invalidateQueries({ queryKey: ["activity", "partner", partner.id] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Change payout details"
        description="Payouts stop until someone other than you approves the new details."
      >
        <div className={styles.form}>
          <Select
            label="Method"
            value={method}
            onValueChange={(v) => setMethod(v as "mpesa" | "bank")}
            options={[
              { value: "mpesa", label: "M-Pesa" },
              { value: "bank", label: "Bank" },
            ]}
          />
          <Input
            label={method === "mpesa" ? "M-Pesa number" : "Account number"}
            inputMode={method === "mpesa" ? "tel" : "numeric"}
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder={method === "mpesa" ? "+254712345678" : undefined}
          />
          <div className={styles.pair}>
            <Input
              label="Account name (optional)"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
            />
            {method === "bank" ? (
              <Input label="Bank code" value={bankCode} onChange={(e) => setBankCode(e.target.value)} />
            ) : null}
          </div>
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!valid} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Save for approval
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
